import {Link, useLocation} from 'react-router-dom';
import type {PromotionMembershipSummary, SalesDynamicsDay, UnitEconomics} from '@ozon-ai/ozon/contracts';
import {CartesianGrid, Line, LineChart, XAxis, YAxis} from 'recharts';
import {Badge} from '@/components/ui/badge.tsx';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card.tsx';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig,
} from '@/components/ui/chart.tsx';
import {Empty, EmptyDescription, EmptyHeader, EmptyTitle} from '@/components/ui/empty.tsx';
import {Separator} from '@/components/ui/separator.tsx';
import {Skeleton} from '@/components/ui/skeleton.tsx';
import {flowAmountClassName, formatFlow, formatUnits} from '../unit-economics-page/format-money';
import {netBillableUnits, scaleAmount} from '../unit-economics-page/waterfall/unit-economics-amount';
import {useProductPageContext} from './product-context';

const chartConfig = {
	sold: {label: 'Продано', color: 'var(--chart-1)'},
	oos: {label: 'Нет в наличии', color: 'var(--chart-4)'},
} satisfies ChartConfig;

type SignalTone = 'critical' | 'attention' | 'positive';

const TONE_LABEL: Record<SignalTone, string> = {
	critical: 'Критично',
	attention: 'Требует внимания',
	positive: 'Положительные изменения',
};

export function ProductOverviewPage() {
	const state = useProductPageContext();
	const {search} = useLocation();
	const unit = state.unitEconomics;
	const previousUnit = state.previousUnitEconomics;
	const days = state.salesDynamics?.statistics ?? [];
	const previousDays = state.previousSalesDynamics?.statistics ?? [];
	const currentPace = pace(days);
	const previousPace = pace(previousDays);
	const currentMargin = marginOf(unit);
	const previousMargin = marginOf(previousUnit);
	const signals = buildSignals({
		unit,
		previousUnit,
		days,
		salesChange: percentChange(state.salesDynamics?.summary.sold, state.previousSalesDynamics?.summary.sold),
		marginDelta: currentMargin != null && previousMargin != null ? currentMargin - previousMargin : null,
	});
	const visibleSignals = signals.slice(0, 5);
	const unitHref = `/ozon/products/${encodeURIComponent(state.offerId)}/analytics/unit${search}`;
	const salesHref = `/ozon/products/${encodeURIComponent(state.offerId)}/analytics/sales${search}`;

	if (!state.product?.ozonSku) {
		return (
			<Empty className="border border-border">
				<EmptyHeader>
					<EmptyTitle>Нет SKU Ozon</EmptyTitle>
					<EmptyDescription>Для этого товара нельзя построить экономику и динамику продаж</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
				<MetricCard
					label="Чистая прибыль"
					value={unit?.netProfit == null ? '—' : formatFlow(unit.netProfit)}
					tone={unit?.netProfit}
					delta={percentChange(unit?.netProfit, previousUnit?.netProfit)}
					previous={previousUnit?.netProfit == null ? null : formatFlow(previousUnit.netProfit)}
				/>
				<MetricCard
					label="Маржа"
					value={currentMargin == null ? '—' : `${formatNumber(currentMargin)}%`}
					tone={currentMargin}
					delta={currentMargin != null && previousMargin != null ? currentMargin - previousMargin : null}
					deltaSuffix=" п.п."
					previous={previousMargin == null ? null : `${formatNumber(previousMargin)}%`}
				/>
				<MetricCard
					label="Продажи"
					value={state.salesDynamics ? formatUnits(state.salesDynamics.summary.sold) : '—'}
					delta={percentChange(state.salesDynamics?.summary.sold, state.previousSalesDynamics?.summary.sold)}
					previous={state.previousSalesDynamics ? formatUnits(state.previousSalesDynamics.summary.sold) : null}
				/>
				<MetricCard
					label="Темп продаж"
					value={currentPace == null ? '—' : `${formatNumber(currentPace)} / день`}
					delta={percentChange(currentPace, previousPace)}
					previous={previousPace == null ? null : `${formatNumber(previousPace)} / день`}
				/>
				<MetricCard
					label="Остаток"
					value={stockLabel(days)}
					delta={null}
					hint={coverLabel(days, currentPace)}
					previous={stockAmount(previousDays) == null ? null : formatUnits(stockAmount(previousDays) ?? 0)}
				/>
				<MetricCard
					label="Продвижение"
					value={membershipValue(state.promotionMembership)}
					delta={percentChange(
						state.promotionMembership?.participatingDays,
						state.previousPromotionMembership?.participatingDays,
					)}
					previous={membershipValue(state.previousPromotionMembership)}
					hint={membershipHint(state.promotionMembership)}
				/>
			</div>

			<section className="flex flex-col gap-3">
				<h2 className="text-base font-medium">Unit-экономика</h2>
				{state.unitEconomicsLoading ? <Skeleton className="h-40 w-full"/> : null}
				{state.unitEconomicsError ? <p className="text-sm text-destructive">{state.unitEconomicsError}</p> : null}
				{unit ? <UnitSnapshot row={unit} previous={previousUnit}/> : null}
				<Link className="text-sm underline" to={unitHref}>Подробнее об unit-экономике</Link>
			</section>

			<section className="flex flex-col gap-3">
				<h2 className="text-base font-medium">Динамика продаж</h2>
				{state.salesDynamicsLoading ? <Skeleton className="h-40 w-full"/> : null}
				{state.salesDynamicsError ? <p className="text-sm text-destructive">{state.salesDynamicsError}</p> : null}
				{days.length > 0 ? <CompactSalesChart days={days}/> : null}
				<p className="text-sm text-muted-foreground">
					{salesCaption(percentChange(state.salesDynamics?.summary.sold, state.previousSalesDynamics?.summary.sold))}
				</p>
			</section>

			<section className="flex flex-col gap-3">
				<h2 className="text-base font-medium">Состояние товара</h2>
				{visibleSignals.length === 0 ? (
					<p className="text-sm text-muted-foreground">За период заметных изменений нет.</p>
				) : (
					<div className="flex flex-col gap-4">
						{(['critical', 'attention', 'positive'] as const).map((tone) => {
							const items = visibleSignals.filter((item) => item.tone === tone);
							if (items.length === 0) return null;
							return (
								<div key={tone} className="flex flex-col gap-2">
									<h3 className="text-sm font-medium">{TONE_LABEL[tone]}</h3>
									{items.map((item) => (
										<p key={item.text} className="text-sm">{item.text}</p>
									))}
								</div>
							);
						})}
					</div>
				)}
				{signals.length > visibleSignals.length ? (
					<Link className="text-sm underline" to={salesHref}>
						Ещё {signals.length - visibleSignals.length}
					</Link>
				) : null}
			</section>
		</div>
	);
}

function MetricCard({
	label,
	value,
	tone,
	delta,
	deltaSuffix = '%',
	hint,
	previous,
}: {
	label: string;
	value: string;
	tone?: number | null;
	delta: number | null;
	deltaSuffix?: string;
	hint?: string;
	previous: string | null;
}) {
	return (
		<Card size="sm">
			<CardHeader>
				<CardDescription>{label}</CardDescription>
			</CardHeader>
			<CardContent className="flex items-stretch gap-3">
				<div className="flex min-w-0 flex-[1.3] flex-col gap-2">
					<CardTitle className={`whitespace-nowrap text-lg tabular-nums ${tone == null ? '' : flowAmountClassName(tone)}`}>{value}</CardTitle>
					{delta != null ? (
						<Badge variant="secondary" className={flowAmountClassName(delta)}>{formatSigned(delta, deltaSuffix)}</Badge>
					) : null}
					{hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
				</div>
				<Separator orientation="vertical"/>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<p className="text-sm text-muted-foreground">Прошлый период</p>
					<p className="whitespace-nowrap text-lg tabular-nums">{previous ?? '—'}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function UnitSnapshot({row, previous}: {row: UnitEconomics; previous: UnitEconomics | null}) {
	const lines = unitLines(row);
	const previousLines = previous ? unitLines(previous) : null;
	return (
		<Card size="sm">
			<CardContent className="flex items-stretch gap-4">
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<p className="text-right text-sm text-muted-foreground">Текущий период</p>
					{lines.map((line) => (
						<p key={line.label} className="flex items-baseline justify-between gap-3 text-sm">
							<span className="text-muted-foreground">{line.label}</span>
							<span className={`whitespace-nowrap tabular-nums ${flowAmountClassName(line.amount, line.expense)}`}>{line.value}</span>
						</p>
					))}
				</div>
				<Separator orientation="vertical"/>
				<div className="flex w-36 shrink-0 flex-col gap-2 sm:w-44">
					<p className="text-sm text-muted-foreground">Прошлый период</p>
					{(previousLines ?? lines).map((line) => (
						<p key={line.label} className={`whitespace-nowrap text-sm tabular-nums ${previousLines ? flowAmountClassName(line.amount, line.expense) : 'text-muted-foreground'}`}>
							{previousLines ? line.value : '—'}
						</p>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function unitLines(row: UnitEconomics): Array<{label: string; value: string; amount: number | null; expense?: boolean}> {
	const units = netBillableUnits(row);
	const perUnit = (value: number | null) => scaleAmount(value, 'perUnit', units);
	const ozonExpenses = row.reward + row.acquiring + row.delivery.total + row.returnFlowLogistic + row.crossDock + row.other + row.premiumSubscription;
	const margin = marginOf(row);
	return [
		{label: 'Выручка на единицу', value: formatFlow(perUnit(row.sales.total)), amount: perUnit(row.sales.total)},
		{label: 'Расходы Ozon на единицу', value: formatFlow(perUnit(ozonExpenses)), amount: perUnit(ozonExpenses)},
		{label: 'Продвижение на единицу', value: formatFlow(perUnit(row.promotion)), amount: perUnit(row.promotion)},
		{label: 'Себестоимость', value: formatFlow(perUnit(row.cogs), true), amount: perUnit(row.cogs), expense: true},
		{label: 'Налог', value: formatFlow(perUnit(row.tax), true), amount: perUnit(row.tax), expense: true},
		{label: 'Чистая прибыль на единицу', value: formatFlow(perUnit(row.netProfit)), amount: perUnit(row.netProfit)},
		{label: 'Маржа', value: margin == null ? '—' : `${formatNumber(margin)}%`, amount: margin},
		{label: 'ROI', value: row.roiPercent == null ? '—' : `${formatNumber(row.roiPercent)}%`, amount: row.roiPercent},
	];
}

function CompactSalesChart({days}: {days: SalesDynamicsDay[]}) {
	const data = days.map((day) => ({
		date: day.date,
		sold: day.sold,
		oos: day.inStock === 0 ? 0 : null,
	}));
	return (
		<ChartContainer config={chartConfig} className="aspect-auto h-40 w-full">
			<LineChart data={data} margin={{left: 8, right: 8, top: 8}}>
				<CartesianGrid vertical={false}/>
				<XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(value: string) => value.slice(8)}/>
				<YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32}/>
				<ChartTooltip content={<ChartTooltipContent labelFormatter={(value) => String(value)}/>}/>
				<Line type="monotone" dataKey="sold" stroke="var(--color-sold)" strokeWidth={2} dot={false}/>
				<Line type="monotone" dataKey="oos" stroke="var(--color-oos)" strokeWidth={0} dot={{r: 3}}/>
			</LineChart>
		</ChartContainer>
	);
}

function buildSignals(input: {
	unit: UnitEconomics | null;
	previousUnit: UnitEconomics | null;
	days: SalesDynamicsDay[];
	salesChange: number | null;
	marginDelta: number | null;
}): Array<{tone: SignalTone; text: string}> {
	const items: Array<{tone: SignalTone; text: string}> = [];
	if (input.unit?.netProfit != null && input.unit.netProfit < 0) {
		items.push({tone: 'critical', text: 'Чистая прибыль за период отрицательная.'});
	}
	const oos = input.days.filter((day) => day.inStock === 0).length;
	if (oos > 0) items.push({tone: 'attention', text: `Товара не было в наличии ${oos} дн.`});
	if (input.unit && input.unit.deliveredUnits > 0 && input.unit.returnedUnits / input.unit.deliveredUnits >= 0.15) {
		items.push({tone: 'attention', text: 'Возвраты составляют 15% или больше от доставленного.'});
	}
	if (input.salesChange != null && input.salesChange <= -20) {
		items.push({tone: 'attention', text: `Продажи снизились на ${formatNumber(Math.abs(input.salesChange))}% к предыдущему периоду.`});
	}
	if (input.salesChange != null && input.salesChange >= 20) {
		items.push({tone: 'positive', text: `Продажи выросли на ${formatNumber(input.salesChange)}% к предыдущему периоду.`});
	}
	if (input.marginDelta != null && input.marginDelta >= 5) {
		items.push({tone: 'positive', text: 'Маржа выросла относительно предыдущего периода.'});
	}
	if (input.previousUnit?.netProfit != null && input.previousUnit.netProfit < 0 && input.unit?.netProfit != null && input.unit.netProfit >= 0) {
		items.push({tone: 'positive', text: 'Чистая прибыль стала неотрицательной.'});
	}
	return items;
}

function marginOf(row: UnitEconomics | null): number | null {
	if (!row || row.netProfit == null || row.sales.total === 0) return null;
	return (row.netProfit / row.sales.total) * 100;
}

function pace(days: SalesDynamicsDay[]): number | null {
	if (days.length === 0) return null;
	const stocked = days.filter((day) => day.inStock == null || day.inStock > 0);
	const base = stocked.length > 0 ? stocked : days;
	return base.reduce((sum, day) => sum + day.sold, 0) / base.length;
}

function stockAmount(days: SalesDynamicsDay[]): number | null {
	const last = [...days].reverse().find((day) => day.inStock != null);
	return last?.inStock ?? null;
}

function stockLabel(days: SalesDynamicsDay[]): string {
	const amount = stockAmount(days);
	if (amount == null) return '—';
	return formatUnits(amount);
}

function coverLabel(days: SalesDynamicsDay[], dailyPace: number | null): string {
	const last = [...days].reverse().find((day) => day.inStock != null);
	if (!last || last.inStock == null) return 'Дни покрытия неизвестны';
	if (dailyPace == null || dailyPace <= 0) return 'Дни покрытия не считаются без темпа продаж';
	return `Покрытие ${formatNumber(last.inStock / dailyPace)} дн.`;
}

function salesCaption(change: number | null): string {
	if (change == null) return 'Сравнение с предыдущим периодом недоступно.';
	const direction = change >= 0 ? 'выросли' : 'снизились';
	return `Продажи ${direction} на ${formatNumber(Math.abs(change))}% к предыдущему периоду той же длины.`;
}

function membershipValue(summary: PromotionMembershipSummary | null): string {
	if (!summary || summary.participatingDays == null) return '—';
	return daysLabel(summary.participatingDays);
}

function membershipHint(summary: PromotionMembershipSummary | null): string | undefined {
	if (!summary) return undefined;
	const status = membershipStatus(summary.now);
	if (summary.observedDays === 0) {
		return status ? `За период снимков нет. ${status}` : 'За период снимков нет';
	}
	const coverage = summary.observedDays < summary.periodDays
		? `Проверено ${summary.observedDays} из ${summary.periodDays} дн.`
		: null;
	return [coverage, status].filter(Boolean).join(' ');
}

function membershipStatus(now: PromotionMembershipSummary['now']): string | null {
	if (now === 'running') return 'Сейчас участвует';
	if (now === 'listed') return 'В кампании, сейчас не идёт';
	if (now === 'absent') return 'Сейчас не участвует';
	return null;
}

function daysLabel(count: number): string {
	const value = Math.abs(Math.trunc(count));
	const mod10 = value % 10;
	const mod100 = value % 100;
	const word = mod10 === 1 && mod100 !== 11
		? 'день'
		: mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
			? 'дня'
			: 'дней';
	return `${formatNumber(count)} ${word}`;
}

function percentChange(current: number | null | undefined, previous: number | null | undefined): number | null {
	if (current == null || previous == null || previous === 0) return null;
	return ((current - previous) / Math.abs(previous)) * 100;
}

function formatNumber(value: number): string {
	return value.toLocaleString('ru-RU', {maximumFractionDigits: 1});
}

function formatSigned(value: number, suffix: string): string {
	const sign = value > 0 ? '+' : '';
	return `${sign}${formatNumber(value)}${suffix}`;
}
